import { Box, Stack, useTheme } from "@mui/material";
import {
  BulletPointsIcon,
  Button,
  DragHandleIcon,
  Drawer,
  Input,
  LibraryIcon,
  Modal,
} from "components";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { ChecklistItem } from "./CheckListItem";
import toast from "react-hot-toast";
import { dayjs } from "utilz";
import { v4 } from "uuid";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { useGlobalStore } from "controller";
import { dateTimeToUtcV2 } from "utilz/helpers";

export const ChecklistSection = forwardRef(
  (
    {
      checklistData,
      onChange,
      onImportClick,
      source,
      isNew = false,
      checklistInputError,
      setChecklistInputError,
    }: {
      checklistData: any;
      onChange: (newData: any) => void;
      onImportClick?: () => void;
      source: "controlCenter" | "other";
      isNew?: boolean;
      checklistInputError: any;
      setChecklistInputError?: (data: any) => void;
    },
    ref,
  ) => {
    const resourceId = useGlobalStore((state) => state.resourceId);
    const timezone = useGlobalStore((state) => state.timezone);
    const theme = useTheme();
    const [newChecklistItemState, setNewChecklistItemState] = useState<any>({
      key: v4(),
      isEditMode: undefined,
    });
    const [titleError, setTitleError] = useState(false);
    const inputRef = useRef<any>(null);

    useEffect(() => {
      if (
        checklistData?.checklist_title &&
        checklistData?.checklist_title.trim?.().length > 0
      ) {
        setTitleError(false);
      }
    }, [checklistData?.checklist_title]);

    const onTitleChange = (event: any) => {
      onChange({
        ...checklistData,
        checklist_title: event.target.value,
      });
    };
    const onRemoveChecklistItem = (item: any, index: number) => {
      if (checklistData.items.length <= 1) {
        onChange({
          ...checklistData,
          items: [],
        });
        return;
      }
      const checklistItems = [...checklistData.items];
      checklistItems.splice(index, 1);
      onChange({
        ...checklistData,
        items: checklistItems,
      });
    };

    const onChecklistItemDataChange = (index: number, newData: any) => {
      const checklistItems = checklistData.items;
      checklistItems[index].data = newData;
      onChange({
        ...checklistData,
        items: checklistItems,
      });
    };
    const onAddChecklistItem = (checklistItem: any) => {
      onChange({
        ...checklistData,
        items: [
          ...checklistData.items,
          {
            key: v4(),
            data: checklistItem,
          },
        ],
      });
      setNewChecklistItemState({
        key: v4(),
        isEditMode: true,
      });
    };
    const onDragEnd = (result: any) => {
      // dropped outside the list
      if (!result.destination) {
        return;
      }

      if (
        result.source.droppableId === "checklist" &&
        result.destination.droppableId === "checklist"
      ) {
        const items = reorder(
          checklistData.items,
          result.source.index,
          result.destination.index,
        );
        onChange({
          ...checklistData,
          items: [...items],
        });
        return;
      }
    };

    const reorder = (list: any, startIndex: number, endIndex: number) => {
      const result = Array.from(list);
      const [removed] = result.splice(startIndex, 1);
      result.splice(endIndex, 0, removed);
      return result;
    };

    useImperativeHandle(ref, () => {
      return {
        submit: () => {
          if (
            source === "controlCenter" &&
            (!checklistData?.checklist_title ||
              checklistData?.checklist_title.trim?.().length <= 0)
          ) {
            setTitleError(true);
            if (checklistData.items <= 0 && setChecklistInputError) {
              setChecklistInputError(true);
            } else if (setChecklistInputError) {
              setChecklistInputError(false);
            }
            return;
          }

          const data = checklistData;
          const filteredChecklistItems = data.items
            .filter(
              (item: any) =>
                item.data.checklist_item_name !== null &&
                item.data.checklist_item_name !== "",
            )
            .map((item: any, index: number) => ({
              checklist_item_name: item.data.checklist_item_name,
              order: index,
              is_important: item.data.is_important,
              ...(source === "other"
                ? {
                    is_completed: item.data.is_completed,
                    completed_by: item.data.is_completed
                      ? item.data.completed_by
                        ? item.data.completed_by
                        : resourceId
                      : null,
                    completed_at: item.data.is_completed
                      ? item.data.completed_at
                        ? item.data.completed_at
                        : dateTimeToUtcV2(dayjs(), timezone)
                      : null,
                  }
                : {}),
            }));

          return {
            ...(data.mapping_id !== undefined
              ? { mapping_id: data.mapping_id }
              : {}),
            ...(source === "controlCenter"
              ? { checklist_title: data.checklist_title }
              : {}),
            checklist_items: [...filteredChecklistItems],
          };
        },
      };
    });

    return (
      <Stack
        sx={{
          // p: "32px 32px 32px 0px",
          p: 0,
        }}
      >
        {source === "controlCenter" && (
          <Input
            name="checklist_title"
            placeholder="Enter a title for your checklist"
            value={checklistData?.checklist_title || ""}
            onChange={onTitleChange}
            required
            fullWidth
            error={titleError}
            errorMessage={
              titleError ? "Please enter a title for your checklist" : ""
            }
          />
        )}
        <Stack
          direction={"row"}
          sx={{
            gap: "16px",
            width: "100%",
            p: "8px 0px 0px 0px",
          }}
        >
          <Button
            endIcon={<LibraryIcon />}
            variant={"secondary"}
            onClick={() => {
              setNewChecklistItemState({
                key: v4(),
                isEditMode: true,
              });
              inputRef?.current?.scrollIntoView({
                behavior: "smooth",
                block: "start",
              });
            }}
          >
            Add Item
          </Button>
          <Button
            endIcon={<LibraryIcon />}
            variant={"secondary"}
            onClick={onImportClick}
          >
            Import
          </Button>
        </Stack>

        <Stack
          sx={{
            width: "100%",
            mt: "16px",
          }}
          direction={"column"}
          gap={"16px"}
        >
          {/*  Drag property may not work in strict mode */}
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="checklist" direction="vertical" type="row">
              {(provided, snapshot) => (
                <Stack
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  sx={{
                    width: "100%",
                    gap: "16px",
                    justifyContent: "start",
                    height: "fit-content",
                    overflowX: "scroll",
                  }}
                >
                  {checklistData.items.map(
                    (checkListItem: any, index: number) => {
                      return (
                        <Box key={checkListItem.key} sx={{ width: "100%" }}>
                          <Draggable
                            key={checkListItem.key}
                            draggableId={checkListItem.key}
                            index={index}
                          >
                            {(provided, snapshot) => (
                              <Stack
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                direction={"row"}
                                sx={{
                                  userSelect: "none",
                                  overflow: "hidden",
                                  alignItems: "center",
                                  ...provided.draggableProps.style,
                                }}
                              >
                                <Box
                                  sx={{
                                    width: "16px",
                                    display: "flex",
                                    justifyContent: "end",
                                  }}
                                >
                                  <DragHandleIcon
                                    sx={{
                                      color: theme.palette.neutral["020"],
                                    }}
                                  />
                                </Box>
                                <Box
                                  sx={{
                                    flex: "1",
                                    userSelect: "none",
                                    background: snapshot.isDragging
                                      ? theme.palette.neutral["B-005"]
                                      : theme.palette.darkBg.high,
                                    height: "100%",
                                    overflow: "hidden",
                                    borderRadius: "4px",
                                  }}
                                >
                                  <ChecklistItem
                                    source={source}
                                    checklistItem={checkListItem.data}
                                    onRemove={() =>
                                      onRemoveChecklistItem(
                                        checkListItem,
                                        index,
                                      )
                                    }
                                    isNew={isNew}
                                    onChange={(newData: any) =>
                                      onChecklistItemDataChange(index, newData)
                                    }
                                  />
                                </Box>
                              </Stack>
                            )}
                          </Draggable>
                        </Box>
                      );
                    },
                  )}
                  {provided.placeholder}
                </Stack>
              )}
            </Droppable>
          </DragDropContext>

          <Stack
            direction={"row"}
            sx={{
              alignItems: "center",
              pl:
                checklistData && checklistData.items.length > 0
                  ? "16px"
                  : "0px",
            }}
          >
            <ChecklistItem
              source={source}
              key={newChecklistItemState.key}
              onChange={onAddChecklistItem}
              isNew
              isEditMode={newChecklistItemState.isEditMode}
              checklistInputError={checklistInputError}
            />
          </Stack>
          <div ref={inputRef}></div>
        </Stack>
      </Stack>
    );
  },
);
